import {
  commonTypes,
  peopleTypes,
  propertiesTypes,
  reportTypes,
  serializedTypes,
} from '@/reports/types';
import formatAddress from '@/utils/formatAddress/formatAddress';
import { ageFromDob, DateUtil } from '@/utils/index';
import isBlacklisted from '@/utils/isBlacklisted';
import { generateJsonPath } from '@/utils/jsonPath';
import {
  formatBuildingPermitsOfPropertyList,
  formatAssessmentOfProperty,
  formatTaxesOfProperty,
  formatLocationOfProperty,
  formatLotAndBuildings,
  sortDeeds,
  sortSexOffenders,
} from './utils';

const dateParser = new DateUtil();

export function SerializeProperty(rawProperty: propertiesTypes.Property) {
  return {
    rawData: rawProperty,
    id: rawProperty.id,
    parcel_address:
      rawProperty.parcel_address &&
      getFormattedAddress(rawProperty.parcel_address),
    parcel_id: rawProperty.parcel_id,
    apn: rawProperty.apn,
    legal: rawProperty.legal,
    owners: rawProperty.owners,
    values: rawProperty.values,
    mailing_address: rawProperty.mailing_address, // TODO: formatAddress(rawProperty.mailing_address)
    state: rawProperty.state,
    municipality: rawProperty.municipality,
    fips: rawProperty.fips,
    county: rawProperty.county,
    alternate_apn: rawProperty.alternate_apn,
    owner_vesting: rawProperty.owner_vesting,
    owner_occupied: rawProperty.owner_occupied,
    taxes: rawProperty.taxes,
    dimensions: rawProperty.dimensions,
    buildings: rawProperty.buildings,
    number_of_buildings: rawProperty.number_of_buildings,
    deeds: rawProperty.deeds,
    residents: mapResidents(rawProperty.residents),
    last_sold_price: rawProperty.last_sold_price,
    last_sold_date: rawProperty.last_sold_date,
    latitude: rawProperty.parcel_address?.geo?.latitude,
    longitude: rawProperty.parcel_address?.geo?.longitude,
    hoas: rawProperty.hoas,
    comparables: rawProperty.comparables,
    courts: rawProperty.courts,
    neighborhood: rawProperty.neighborhood,
    building_permits: rawProperty.building_permits,
    assessments: rawProperty?.values?.assessments,
    equities: rawProperty?.values?.equities,
    history: rawProperty?.values?.history_values,
    market: rawProperty?.values?.market,
    bills: rawProperty?.taxes?.bills,
    exemptions: rawProperty?.taxes?.exemptions,
    assessedLandValue: rawProperty?.values?.assessments?.[0]?.land,
    assessedImprovementValue:
      rawProperty?.values?.assessments?.[0]?.improvements,
    taxAmount: rawProperty?.taxes?.bills?.[0]?.amount,
    firstBuilding: rawProperty.buildings?.[0],

    // Report sections
    locationOfProperty: formatLocationOfProperty(rawProperty),
    assessmentOfProperty: formatAssessmentOfProperty(rawProperty),
    taxesOfProperty: formatTaxesOfProperty(rawProperty),
    taxHistory: rawProperty?.taxes,
    buildingPermitsOfProperty: formatBuildingPermitsOfPropertyList(rawProperty),
    lotAndBuildingDetails: formatLotAndBuildings(rawProperty),
  } as serializedTypes.SerializedProperty;
}

export function SerializePropertyReport(rawPropertyReport: reportTypes.Report) {
  const entities = rawPropertyReport?.entities;
  const rawProperties = entities?.properties?.filter(
    (property: propertiesTypes.Property) =>
      property?.id || property?.parcel_address
  );

  const serializedProperties = rawProperties?.map(
    (rawProperty: propertiesTypes.Property) => SerializeProperty(rawProperty)
  );

  let isPropertyBlacklisted = false;
  if (rawPropertyReport?.meta?.report_info?.proxy_flags) {
    isPropertyBlacklisted = isBlacklisted(
      rawPropertyReport?.meta.report_info.proxy_flags
    );
  }

  const reportData = rawPropertyReport?.supporting;
  const location = reportData?.location;
  // TODO: determine if maping residence address for sex offnders is still needed
  const sexOffenders = sortSexOffenders(reportData?.area?.sex_offenders || []);
  const preforeclosures = reportData?.preforeclosures || []; // TODO: .map(mapPreforeclosures);
  const listings = reportData?.listings || []; // TODO: .map(mapListings);
  const rawRelatedProperties = entities?.options?.properties || [];
  const serializedRelatedProperties = rawRelatedProperties.map(
    (rawRelatedProperty) =>
      SerializeProperty(rawRelatedProperty as propertiesTypes.Property)
  );

  let relatedProperties = serializedRelatedProperties;
  if (relatedProperties) {
    relatedProperties = relatedProperties.map(
      mapProperties('entities.options.properties')
    );
  }

  let properties = serializedProperties;
  if (properties) {
    properties = properties.map(mapProperties('entities.properties'));
  }

  if (!properties || properties.length === 0) {
    const firstRelatedProperty = relatedProperties.shift();

    properties = !firstRelatedProperty ? [] : [{ ...firstRelatedProperty }];
  }

  const serializedPropertyReport = {
    rawData: rawPropertyReport,
    meta: rawPropertyReport?.meta,
    alert_me: rawPropertyReport?.meta?.alert_me || false,
    created_at: rawPropertyReport?.meta?.created_at,
    report_upgraded: rawPropertyReport?.meta?.report_upgraded,
    id: rawPropertyReport?.meta?.permalink,
    isBlacklisted: isPropertyBlacklisted,
    debug_menu: rawPropertyReport?.debug_menu,
    properties,
    related_properties: relatedProperties,
    preforeclosures,
    listings,
    schools: reportData?.area?.schools || [],
    sex_offenders: sexOffenders,
    address: location?.address,
    street: rawPropertyReport?.meta?.search_data?.address,
    city: location?.city,
    country: location?.country,
    state: location?.state,
    zip: location?.zip5,
    latitude: location?.latitude,
    longitude: location?.longitude,
    hasResult:
      (serializedProperties?.length ?? 0 > 0) || sexOffenders.length > 0,
    mrRogers: getMrRogersFlags(properties[0]),
  };

  return serializedPropertyReport;
}

function getFormattedAddressArray(addresses: commonTypes.Addresses = []) {
  return addresses.map((address) => {
    return getFormattedAddress(address);
  });
}

function getFormattedAddress(address: serializedTypes.Address) {
  if (address) {
    address.parts = address.parsed;
    address = formatAddress(address);
  }
  return address;
}

function mapNames(names: commonTypes.Names = []) {
  return names.map((name) => {
    name.parsed = name.parsed || {
      first: '',
      last: '',
      middle: '',
      salutation: '',
      suffix: '',
      professional_suffix: '',
    };
    return name;
  });
}

const mapAssessments = (
  assessments: propertiesTypes.Assessments = [],
  propertyJsonPath: string
) => {
  return assessments.map((assesment, index: number) => {
    assesment.jsonPath = generateJsonPath(
      index,
      propertyJsonPath,
      'values.assessments'
    );
    return assesment;
  });
};

export function mapResidents(residents: propertiesTypes.Residents = []) {
  return residents.map((resident) => {
    resident.addresses = getFormattedAddressArray(resident.addresses);
    resident.names = mapNames(resident.names);
    resident.dobs = mapResidentsDobs(resident.dobs);
    return resident;
  });
}

function mapProperties(propertyRootJsonPath: string) {
  return function (
    property: serializedTypes.SerializedProperty,
    index: number
  ) {
    property.jsonPath = `$.${propertyRootJsonPath}[${index}]`;
    property.owners = mapOwners(
      property.owners,
      property.jsonPath
    ) as serializedTypes.SerializedProperty['owners'];
    property.residents = mapResidentsJsonPaths(
      property.residents,
      property.jsonPath
    );
    property.assessments = mapAssessments(
      property.assessments,
      property.jsonPath
    );
    property.buildings = mapBuildings(property.buildings, property.jsonPath);
    property.deeds = mapDeeds(property.deeds, property.jsonPath);
    property.courts = mapCourts(property.courts);
    property.equities = mapEquities(property.equities, property.jsonPath);
    return property;
  };
}

function mapResidentsDobs(dobs: commonTypes.DatesWithMeta = []) {
  return dobs.map((dob) => {
    if (
      dob?.date?.parsed?.year &&
      dob?.date?.parsed?.month &&
      dob?.date?.parsed?.day &&
      dob?.date?.full
    ) {
      dob.age = !isNaN(ageFromDob(dob?.date?.full)) // TODO : check if this is needed, I didn't find any usage of this
        ? ageFromDob(dob?.date?.full)
        : undefined;
    }

    return dob;
  });
}

function mapResidentsJsonPaths(
  residents: propertiesTypes.Residents = [],
  path: string
) {
  return residents.map((resident, index: number) => {
    resident.jsonPath = generateJsonPath(index, path, 'residents');
    return resident;
  });
}

export function mapOwners(
  owners: serializedTypes.SerializedProperty['owners'] = [],
  path: string
) {
  return owners.map((owner, index: number) => {
    if ('names' in owner) {
      owner.names = mapNames(owner.names);
    } else if (owner.identity) {
      owner.identity.names = mapNames(owner.identity.names);
    }
    owner.jsonPath = generateJsonPath(index, path, 'owners');
    return owner;
  });
}

const mapBuildings = (
  buildings: propertiesTypes.Buildings = [],
  propertyJsonPath: string
) => {
  return buildings.map((building, index: number) => {
    building.jsonPath = generateJsonPath(index, propertyJsonPath, 'buildings');

    if (building.codes) {
      building.codes.jsonPath = generateJsonPath(
        null,
        building.jsonPath,
        'codes'
      );
    }
    if (building.rooms) {
      building.rooms.jsonPath = generateJsonPath(
        null,
        building.jsonPath,
        'rooms'
      );
    }
    return building;
  });
};

const mapDeeds = (
  deeds: propertiesTypes.Deeds = [],
  propertyJsonPath: string
) => {
  return deeds.sort(sortDeeds).map((deed, index: number) => {
    const totalSellers = deed?.transfer?.sellers?.length || 0;
    return {
      ...deed,
      jsonPath: generateJsonPath(index, propertyJsonPath, 'deeds'),
      isNewLoan: totalSellers === 0,
    };
  });
};

const mapCivilDebtors = (debtors: peopleTypes.Individuals = []) => {
  return debtors.map((debtor) => {
    debtor.names = mapNames(debtor.names);
    debtor.addresses = getFormattedAddressArray(debtor.addresses);
    return debtor;
  });
};

const checkIfCivilDebtorsHaveNames = (
  debtors: peopleTypes.Individuals = []
): boolean => {
  let hasDebtorsNames = false;
  debtors.forEach((debtor) => {
    if (debtor.names.length) {
      hasDebtorsNames = true;
    }
  });

  return hasDebtorsNames;
};

const checkIfCivilDebtorsHaveAddresses = (
  debtors: peopleTypes.Individuals = []
): boolean => {
  let hasDebtorsAddresses = false;

  debtors.forEach((debtor) => {
    if (debtor.addresses.length) {
      hasDebtorsAddresses = true;
    }
  });

  return hasDebtorsAddresses;
};

const mapCivilFilings = (filings: peopleTypes.CivilFilings = []) => {
  return filings.map((filing) => {
    const serializedFiling: serializedTypes.SerializedCivilFiling = filing;

    if (filing.filing_date && filing.filing_date.full) {
      serializedFiling.filingDate = dateParser.parseDateFromString(
        filing.filing_date.full,
        'MM/dd/yyyy'
      );
    }

    return serializedFiling;
  });
};

const mapCivils = (civils: peopleTypes.Civils = []) => {
  return civils.map((civil) => {
    const serializedCivil: serializedTypes.SerializedCivil = { ...civil };

    if (civil.filing_date && civil.filing_date.full) {
      serializedCivil.filingDate = dateParser.parseDateFromString(
        civil.filing_date.full,
        'MM/dd/yyyy'
      );
    }

    serializedCivil.debtors = mapCivilDebtors(civil.debtors);
    serializedCivil.hasDebtorsNames = checkIfCivilDebtorsHaveNames(
      civil.debtors
    );
    serializedCivil.hasDebtorsAddresses = checkIfCivilDebtorsHaveAddresses(
      civil.debtors
    );
    serializedCivil.filings = mapCivilFilings(civil.filings);

    return serializedCivil;
  });
};

const mapCourts = (courts: propertiesTypes.CourtRecord = { civils: [] }) => {
  const newCourts = courts;
  newCourts.civils = mapCivils(newCourts.civils);
  return newCourts;
};

const getMrRogersFlags = (property: serializedTypes.SerializedProperty) => {
  const mrRogersData = {
    hasDataToRequestCensusData: false,
    hasDataToRequestHazardsData: false,
  };

  if (property) {
    const address = property.parcel_address
      ? property.parcel_address
      : property.mailing_address;
    mrRogersData.hasDataToRequestCensusData =
      property.fips !== '' || !!address?.parsed?.zip5;
    mrRogersData.hasDataToRequestHazardsData =
      (address?.geo?.latitude && address?.geo?.longitude) !== undefined;
  }

  return mrRogersData;
};

function mapEquities(
  equities: propertiesTypes.Equities,
  propertyJsonPath: string
) {
  equities = equities || [];
  return equities.map((equity, index) => {
    return {
      ...equity,
      jsonPath: generateJsonPath(index, propertyJsonPath, 'values.equities'),
    };
  });
}
